import {BaseCommand} from './base.command';
import {ClipboardSnapshot, CommandType} from '../interfaces/command.interface';
import {UndoLabelMetadata, UndoObjectType} from '../undo-label.utils';

interface ClipboardCommandSnapshot {
  clipboard: ClipboardSnapshot;
}

/**
 * ClipboardCommand handles copy operations with clipboard state tracking.
 * Allows undoing copy operations to restore the previous clipboard state.
 */
export class ClipboardCommand extends BaseCommand<ClipboardCommandSnapshot> {
  constructor(
    type: 'copy' | 'cut',
    beforeClipboard: ClipboardSnapshot,
    afterClipboard: ClipboardSnapshot,
    private elementCount: number,
    nodeCount: number = 0,
    linkCount: number = 0
  ) {
    // Determine object type based on element counts
    let objectType: UndoObjectType;
    if (nodeCount === 1 && linkCount === 0) {
      objectType = 'Node';
    } else if (nodeCount === 0 && linkCount === 1) {
      objectType = 'Link';
    } else {
      objectType = 'Selection';
    }

    const verb = type === 'copy' ? 'Copy' : 'Cut';
    const labelMetadata: UndoLabelMetadata = {
      object: objectType,
      verb: verb,
      elementCount: objectType === 'Selection' ? elementCount : undefined
    };

    super(
      type as CommandType,
      `${objectType} ${verb}`,
      {
        before: {clipboard: beforeClipboard},
        after: {clipboard: afterClipboard}
      },
      labelMetadata
    );
  }

  async execute(): Promise<void> {
    // Set clipboard to the "after" state
    const afterContent = this.snapshot.after.clipboard.content;
    if (afterContent !== null) {
      localStorage.setItem('clipboard', afterContent);
    } else {
      localStorage.removeItem('clipboard');
    }
  }

  async undo(): Promise<void> {
    // Restore clipboard to the "before" state
    const beforeContent = this.snapshot.before.clipboard.content;
    if (beforeContent !== null) {
      localStorage.setItem('clipboard', beforeContent);
    } else {
      localStorage.removeItem('clipboard');
    }
  }
}
